import { useCallback, useMemo } from 'react';

import { ANALYTICS_SOURCE, BYLINE_ACTION_SUBJECT_ID } from '../analytics.ts';
import {
	type TwgResultSurface,
	useTwgResultActioned,
	useTwgResultViewed,
} from '../twgResultAnalytics.ts';

import type { useBylineConnectionsData } from './useBylineConnectionsData.ts';

type BylineConnectionsData = ReturnType<typeof useBylineConnectionsData>;

type BylineTwgResultEventsOptions = {
	visibility: {
		hiddenBySsr: boolean;
		isResolving: boolean;
		shouldRenderByline: boolean;
	};
	data: {
		incoming: Pick<BylineConnectionsData['incoming'], 'twgLinks' | 'loading'>;
		relatedContent: Pick<BylineConnectionsData['relatedContent'], 'links' | 'loading'>;
	};
};

/**
 * The byline as a Teamwork Graph surface. Only its Appears in and Related content rows come from
 * the graph (Cypher queries over AGG GraphQL); outgoing links are read from the page itself and
 * remote links from Jira, so neither counts toward its `twgResult` events.
 */
const BYLINE_TWG_RESULT_SURFACE: TwgResultSurface = {
	actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
	source: ANALYTICS_SOURCE,
	twgSource: 'Cypher',
	queryMethod: 'GraphQL',
};

/**
 * Reports the byline's graph rows to Teamwork Graph adoption analytics: `twgResult viewed` once
 * they are on screen, and the returned callback fires `twgResult actioned` when the menu opens.
 */
export const useBylineTwgResultEvents = ({
	visibility,
	data,
}: BylineTwgResultEventsOptions): (() => void) => {
	const { hiddenBySsr, isResolving, shouldRenderByline } = visibility;
	const { twgLinks: twgIncomingLinks, loading: incomingLinksLoading } = data.incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = data.relatedContent;
	const attributes = useMemo(
		() => ({
			incomingLinkCount: twgIncomingLinks.length,
			relatedContentCount: relatedContentLinks.length,
		}),
		[twgIncomingLinks.length, relatedContentLinks.length],
	);
	const twgResultCount = attributes.incomingLinkCount + attributes.relatedContentCount;
	// The view also waits for both graph sources: outgoing links alone can show the byline before
	// they answer, and the event must count what the graph actually returned.
	const isShown =
		!hiddenBySsr &&
		!isResolving &&
		shouldRenderByline &&
		!incomingLinksLoading &&
		!relatedContentLoading;
	useTwgResultViewed(BYLINE_TWG_RESULT_SURFACE, isShown ? twgResultCount : 0, attributes);

	const fireActioned = useTwgResultActioned(BYLINE_TWG_RESULT_SURFACE);
	return useCallback(
		() => fireActioned(twgResultCount, { ...attributes, twgAction: 'openMenu' }),
		[fireActioned, twgResultCount, attributes],
	);
};
